import React from 'react'
import { useSelector } from 'react-redux'
import { getVideo } from 'Utils/videoUrls'
import {
  useGetVideoByIdQuery,
  useGetPanoramaByIdQuery,
  useGetPhotoByIdQuery,
} from 'Services/institution'
import { Box, Skeleton } from '@mui/material'
import { useMediaDetails } from 'Hooks/useMediaDetails'
import DOMPurify from 'dompurify'

interface MainMediaProps {
  id: number
  type: string
  info?: boolean
  pointMediaId?: number
  stopId?: number
}

export const MainMedia = ({ id, type, info, pointMediaId, stopId }: MainMediaProps) => {
  const { locId, instId } = useSelector((state: any) => state.location)

  const { data: videoData, isLoading: videoLoading } = useGetVideoByIdQuery(
    { instId, locId, videoId: id },
    { skip: type !== 'video' }
  )
  const video = videoData?.resources?.video
  const approvedVideo = video?.status === '1' && video?.approved === '1' 

  const {
    data: photoData,
    isLoading: photoLoading,
    isSuccess: photoSuccess,
  } = useGetPhotoByIdQuery(
    { instId, locId, photoId: id },
    { skip: type !== 'photo' }
  )
  const photo = photoData?.resources?.photo
  const approvedPhoto = photo?.status === '1' 

  const {
    data: panoramaData,
    isLoading: panoramaLoading,
    isSuccess: panoramaSuccess,
  } = useGetPanoramaByIdQuery(
    { instId, locId, panoramaId: id },
    { skip: type !== 'panorama' }
  )
  const panorama = panoramaData?.resources?.panorama
  const approvedPanorama = panorama?.status === '1'

  const { photoAssetUrl: pointAssetUrl } = useMediaDetails(stopId)

  const youVisitAssetUrl =
    photoData?.resources?.metadata?.path?.youvisit?.slice(
      0,
      photoData?.resources?.metadata?.path?.youvisit.indexOf('/media')
    ) ||
    panoramaData?.resources?.metadata?.path?.slice(
      0,
      panoramaData?.resources?.metadata?.path?.indexOf('/media')
    )

  if (videoLoading || photoLoading || panoramaLoading) {
    return <Skeleton variant='rectangular' width={'100%'} height={450} />
  }

  if (type === 'video' && video) {
    return (
      <div>
        {getVideo(video)}
        <br />
        <br />
      </div>
    )
  }

  const isPointMedia = !type && pointMediaId && pointAssetUrl

  if (
    (type === 'photo' && photoSuccess) ||
    (type === 'panorama' && panoramaSuccess) ||
    (isPointMedia)
  ) {
    const mediaType = type === 'panorama' ? 'panoramas' : 'photos'
    const mediaId = type === 'panorama' ? panorama?.id : photo?.photoid
    const mediaTitle = type === 'panorama' ? panorama?.title : photo?.title
    const mediaDescription =
      type === 'panorama' ? panorama?.description : photo?.description
    const mediaAccessibilityDescription =
      type === 'panorama'
        ? panorama?.accessibility_description
        : photo?.accessibility_description
    
    // media without type uses point media url structure
    const mediaUrl = type ? `${youVisitAssetUrl}/media/${locId}/${mediaType}/${mediaId}/1024.jpg` : `${pointAssetUrl}/media/${locId}/tour/${pointMediaId}/1024_front.jpg`

    if (approvedVideo || approvedPanorama || approvedPhoto || isPointMedia) return (
      <>
        {info && <h3>{mediaTitle}</h3>}
        <Box
          tabIndex={0}
          aria-label={`${mediaTitle + '.'} ${
            mediaAccessibilityDescription || mediaDescription || ''
          }`}
          component='div'
          sx={{
            width: '100%',
            height: 'auto',
            background: '#f2f3f4',
            textAlign: 'center',
          }}
        >
          {mediaUrl && (mediaId || pointMediaId) && (
            <Box
              component='img'
              src={mediaUrl}
              alt='Main media'
              sx={{ maxHeight: '450px', maxWidth: '100%' }}
            />
          )}
        </Box>
        {info && (
          <p style={{ margin: 0 }} aria-label={mediaAccessibilityDescription}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                mediaDescription || mediaAccessibilityDescription,
                { ADD_ATTR: ['target'] }
              ),
            }}
          />
        )}
      </>
    )
  }

  return null
}
